import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {ButtonTheme} from 'common/components/button/constants';
import {ProjectFeatureFlag} from 'common/report/constants';
import {IronContextRegistry} from 'common/report/context-registry';

import css from './index.inline.styl';
import {template} from './template';

interface BoardsSaveButtonAttrs {
  'selected-dashboard-id': number;
  'user-id': number;
  disabled: boolean;
  tooltip?: string;
  'align-right'?: boolean;
  'search-placeholder': string;
  loading?: boolean;
  'has-unsaved-changes': boolean;
}

interface BoardsSaveButtonJsxProps {
  attrs?: BoardsSaveButtonAttrs;
  props?: {
    dashboardController: DashboardSelectorController;
    recentDashboardIds?: number[];
  };
  on?: {
    save: (ev?: CustomEvent) => void;
  };
}

interface BoardsSaveButtonState {
  isOpen: boolean;
  recentDashboardIds: number[];
}

export class BoardsSaveButton extends Component<
  BoardsSaveButtonState,
  BoardsSaveButtonAttrs,
  any,
  any,
  IronContextRegistry
> {
  private _dashboardController: DashboardSelectorController;

  static get attrsSchema(): AttrsSchema<BoardsSaveButtonAttrs> {
    return {
      ...super.attrsSchema,
      'selected-dashboard-id': `number`,
      'user-id': `number`,
      disabled: `boolean`,
      'align-right': `boolean`,
      tooltip: `string`,
      'search-placeholder': `string`,
      loading: `boolean`,
      'has-unsaved-changes': `boolean`,
    };
  }

  get config() {
    return {
      defaultState: {
        isOpen: false,
        recentDashboardIds: [],
      },
      contexts: [`currentProject` as const],
      useShadowDom: true,
      css,
      template,
      helpers: {
        handleSaveButtonClicked: () => {
          if (this.attr(`selected-dashboard-id`)) {
            this.dispatchEvent(new CustomEvent(`save`, {detail: {dashboardId: this.attr(`selected-dashboard-id`)}}));
          } else {
            this.update({isOpen: true});
          }
        },
        handleDashboardSelected: (ev) => {
          this.update({isOpen: false});
          this.dispatchEvent(new CustomEvent(`save`, {detail: {dashboardId: ev.detail.id}}));
          ev.preventDefault();
        },
        handleMenuChanged: (ev) => {
          this.update({isOpen: ev.detail.state === `open`});
          this.dispatchEvent(new CustomEvent(`menuchange`, {detail: ev.detail}));
        },
      },
    };
  }

  set dashboardController(controller: DashboardSelectorController) {
    controller.getDashboards();
    this._dashboardController = controller;
    this.update();
  }
  get dashboardController() {
    return this._dashboardController;
  }

  set recentDashboardIds(value) {
    this.update({recentDashboardIds: value});
  }

  get currentProject() {
    return this.getContext(`currentProject`).get().value;
  }

  icon() {
    return this.attr(`loading`)
      ? `spinner`
      : this.fiwFeatureEnabled() && !this.attr(`has-unsaved-changes`)
      ? `checkmark`
      : ``;
  }

  buttonTheme() {
    return this.attr(`has-unsaved-changes`) && this.fiwFeatureEnabled() && !this.attr(`disabled`)
      ? ButtonTheme.Primary
      : ButtonTheme.Secondary;
  }

  fiwFeatureEnabled() {
    return this.currentProject?.hasFeatureFlag(ProjectFeatureFlag.FastIterationWorkflow);
  }
}

export const BoardsSaveButtonEl = defineAndMakeJsxFactory<BoardsSaveButtonJsxProps>(
  `mp-boards-save-button`,
  BoardsSaveButton,
);
export const BoardsSaveButtonReact = panel2react<BoardsSaveButtonJsxProps>(`mp-boards-save-button`, BoardsSaveButton);
